import {RadioGroup, Typography} from '@ezcater/tapas';
import {Form, Formik} from 'formik';
import useTranslation from 'next-translate/useTranslation';

import Modal from '@/components/ui/Modal';
import {useCorpAccountSelectionModalContext} from './CorpAccountSelectionModalProvider';
import useCorpAccountSelection from './useCorpAccountSelection';
import {getCorpDisplayName} from './utils';

type CorpAccountSelectionModalValues = {
  selectedCorpAccountUuid: string;
};

const CORP_ACCOUNT_SELECTION_FORM_ID = 'corp-account-selection-form';

const CorpAccountSelectionModal: React.FC = () => {
  const {changeSelectedCorpAccountUuid, sortedCorpAccounts, currentCorpAccountUuid, loading} =
    useCorpAccountSelection();
  const {isModalOpen, toggleCorpAccountSelectionModal} = useCorpAccountSelectionModalContext();
  const {t} = useTranslation('app-bar');

  if (loading) {
    return null;
  }

  const handleClose = () => {
    toggleCorpAccountSelectionModal(false);
  };

  const submitModal = (values: CorpAccountSelectionModalValues) => {
    handleClose();
    changeSelectedCorpAccountUuid(values.selectedCorpAccountUuid);
  };

  return (
    <Formik<CorpAccountSelectionModalValues>
      initialValues={{
        selectedCorpAccountUuid: currentCorpAccountUuid ?? sortedCorpAccounts?.[0]?.uuid ?? '',
      }}
      onSubmit={submitModal}
    >
      {({isSubmitting, isValid, setFieldValue, values}) => (
        <Modal
          cancelLabel={t('menus.corporateAccounts.accountPicker.modal.closeLabel')}
          onOpenChange={open => !open && handleClose()}
          open={isModalOpen}
          primaryAction={{
            closeOnComplete: false,
            disabled: !isValid || isSubmitting,
            form: CORP_ACCOUNT_SELECTION_FORM_ID,
            label: t('menus.corporateAccounts.accountPicker.modal.submitLabel'),
            loading: isSubmitting,
            type: 'submit',
          }}
          slotProps={{
            content: {className: 'w-[480px]'},
            header: {className: 'border-0'},
          }}
          title={t('menus.corporateAccounts.accountPicker.modal.title')}
        >
          <Form id={CORP_ACCOUNT_SELECTION_FORM_ID}>
            <RadioGroup.Root
              render={<fieldset className="flex flex-col gap-4" />}
              name="selectedCorpAccountUuid"
              onValueChange={value => setFieldValue('selectedCorpAccountUuid', value)}
              value={values.selectedCorpAccountUuid}
            >
              {sortedCorpAccounts?.map(corpAccount => (
                <label
                  className="inline-flex items-start justify-start gap-3"
                  key={corpAccount.uuid}
                >
                  <RadioGroup.Item className="shrink-0" value={corpAccount.uuid} />
                  <div className="inline-flex flex-col items-start justify-start">
                    <div>{getCorpDisplayName(corpAccount)}</div>
                    {corpAccount.uuid === currentCorpAccountUuid && (
                      <Typography className="text-peppercorn-500" variant="body-sm">
                        {t('menus.corporateAccounts.accountPicker.modal.currentSelection')}
                      </Typography>
                    )}
                  </div>
                </label>
              ))}
            </RadioGroup.Root>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default CorpAccountSelectionModal;
